import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import Modal from '../../components/Modal';
import reversalsService from '../../services/reversals.service';
import withdrawalsService from '../../services/withdrawals.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';
import type { ReversalRequest, ReversalStatus, WithdrawalRequest } from '../../types/finance';

const STATUS_TONE: Record<ReversalStatus, BadgeTone> = {
  pending: 'info',
  approved: 'success',
  rejected: 'danger',
};

export default function AgentReversals(): JSX.Element {
  const [reversals, setReversals] = useState<ReversalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPropose, setShowPropose] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await reversalsService.list({ size: 50 });
      setReversals(res.items);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load your reversals.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  return (
    <>
      <PageHeader
        title="Reversals"
        subtitle="Propose a reversal of an approved withdrawal. A manager reviews before the credit posts to the ledger."
        actions={
          <button
            type="button"
            onClick={() => setShowPropose(true)}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: gradients.gold, color: colors.navy }}
          >
            Propose reversal
          </button>
        }
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            background: colors.successBg,
            border: `1px solid ${colors.successBorder}`,
            color: colors.successText,
          }}
        >
          {flash}
        </div>
      )}

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{
            background: colors.dangerBg,
            border: `1px solid ${colors.dangerBorder}`,
            color: colors.danger,
          }}
        >
          {error}
        </div>
      )}

      <ReversalTable reversals={reversals} loading={loading} />

      {showPropose && (
        <ProposeReversalModal
          onClose={() => setShowPropose(false)}
          onSubmitted={(msg) => {
            setShowPropose(false);
            setFlash(msg);
            globalThis.setTimeout(() => setFlash(null), 5000);
            reload();
          }}
        />
      )}
    </>
  );
}

interface TableProps {
  readonly reversals: ReversalRequest[];
  readonly loading: boolean;
}

function ReversalTable({ reversals, loading }: TableProps): JSX.Element {
  if (loading) {
    return <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>;
  }
  if (reversals.length === 0) {
    return (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        You have not proposed any reversals yet.
      </div>
    );
  }
  return (
    <div
      className="overflow-hidden rounded-2xl"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <table className="w-full text-sm">
        <thead style={{ background: colors.canvas, color: colors.muted }}>
          <tr>
            <th className="text-left font-medium px-4 py-3">Reference</th>
            <th className="text-right font-medium px-4 py-3">Amount</th>
            <th className="text-left font-medium px-4 py-3">Reason</th>
            <th className="text-left font-medium px-4 py-3">Status</th>
            <th className="text-left font-medium px-4 py-3">Proposed</th>
          </tr>
        </thead>
        <tbody>
          {reversals.map((r, idx) => (
            <tr
              key={r.id}
              style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}
            >
              <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.muted }}>
                {r.reference_number ?? '—'}
              </td>
              <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                {formatMoney(r.amount, r.currency_code)}
              </td>
              <td className="px-4 py-3 max-w-xs truncate" style={{ color: colors.inkSoft }}>
                {r.reason}
              </td>
              <td className="px-4 py-3">
                <StatusBadge tone={STATUS_TONE[r.status]}>{r.status}</StatusBadge>
              </td>
              <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                {formatDateTime(r.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface ProposeProps {
  readonly onClose: () => void;
  readonly onSubmitted: (flashMessage: string) => void;
}

function ProposeReversalModal({ onClose, onSubmitted }: ProposeProps): JSX.Element {
  const [approvedWithdrawals, setApprovedWithdrawals] = useState<WithdrawalRequest[] | null>(null);
  const [withdrawalId, setWithdrawalId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await withdrawalsService.list({ status: ['approved'], size: 100 });
        if (!cancelled) setApprovedWithdrawals(res.items);
      } catch (e: unknown) {
        if (!cancelled) setError(getApiErrorMessage(e, 'Could not load approved withdrawals.'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selected = approvedWithdrawals?.find((w) => w.id === withdrawalId);
  const trimmedReason = reason.trim();
  const formValid = withdrawalId && trimmedReason.length >= 5 && trimmedReason.length <= 1000;

  const submit = async (): Promise<void> => {
    if (!formValid || !selected) return;
    setBusy(true);
    setError(null);
    try {
      await reversalsService.propose({
        withdrawal_request_id: withdrawalId,
        reason: trimmedReason,
      });
      onSubmitted(`Reversal proposed for ${formatMoney(selected.amount, selected.currency_code)} — awaiting manager review.`);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not submit the reversal.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Propose reversal
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Select an approved withdrawal to reverse (full amount only). A manager
        reviews before the credit posts back to the ledger.
      </p>

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      <div className="space-y-4">
        <Field label="Approved withdrawal">
          <select
            value={withdrawalId}
            onChange={(e) => setWithdrawalId(e.target.value)}
            disabled={!approvedWithdrawals || busy}
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            <option value="">— Select withdrawal —</option>
            {(approvedWithdrawals ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.reference_number ?? w.id.slice(0, 8)} · {formatMoney(w.amount, w.currency_code)} · {w.user_email}
              </option>
            ))}
          </select>
        </Field>

        {selected && (
          <div
            className="p-3 rounded-lg text-xs"
            style={{ background: colors.surfaceMuted, color: colors.textMuted }}
          >
            <div className="font-semibold" style={{ color: colors.textPrimary }}>
              {selected.product_name} — {formatMoney(selected.amount, selected.currency_code)}
            </div>
            <div>Ref: {selected.reference_number ?? '—'} · Approved {formatDateTime(selected.reviewed_at ?? selected.created_at)}</div>
          </div>
        )}

        <Field label="Reason for reversal">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            rows={4}
            maxLength={1000}
            placeholder="e.g. Client request — erroneous disbursement."
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.border}` }}
          />
          <div className="text-xs mt-1" style={{ color: colors.mutedSoft }}>
            {trimmedReason.length}/1000 · minimum 5 characters
          </div>
        </Field>
      </div>

      <div className="flex justify-end gap-2 mt-6">
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !formValid}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: busy || !formValid ? colors.disabledBg : gradients.gold,
            color: busy || !formValid ? colors.disabledText : colors.navy,
          }}
        >
          {busy ? 'Submitting…' : 'Submit proposal'}
        </button>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        {label}
      </label>
      {children}
    </div>
  );
}
